import React from 'react';
import { Target, Eye, Award, Shield, CheckCircle2, Users, Factory, Lightbulb } from 'lucide-react';
import { companyInfo, mision, vision, valores, stats } from '../data/mockData';

export const Nosotros = () => {
  const historia = {
    year: "1998",
    story: "MAESI nació de la necesidad identificada en la industria del envasado: falta de soluciones confiables, personalizadas y de calidad. Fundada por un grupo de ingenieros apasionados por la innovación, comenzamos fabricando agitadores y llenadoras básicas para pequeñas empresas locales."
  };

  const evolucion = [
    {
      period: "1998-2005",
      title: "Fundación y Crecimiento",
      description: "Establecimiento en Zapopan, Jalisco. Primeros clientes en la industria alimentaria y química."
    },
    {
      period: "2006-2015",
      title: "Expansión Nacional",
      description: "Ampliación de línea de productos: etiquetadoras, transportadores y tanques de proceso. Presencia en toda la República Mexicana."
    },
    {
      period: "2016-Presente",
      title: "Liderazgo e Innovación",
      description: "Consolidación como referente en maquinaria industrial personalizada. Más de 500 proyectos exitosos y tecnología de vanguardia."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Conoce <span className="text-blue-300">MAESI</span>
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Más de dos décadas transformando la industria mexicana con soluciones de maquinaria de clase mundial
            </p>
          </div>
        </div>
      </section>

      {/* Historia */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-900 font-bold rounded-full mb-6">
                Nuestra Historia
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                El Origen de una{' '}
                <span className="text-blue-600">Empresa Líder</span>
              </h2>
              <div className="mb-6">
                <div className="text-6xl font-bold text-blue-600 mb-2">{historia.year}</div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {historia.story}
                </p>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                Desde entonces, nuestro compromiso ha sido inquebrantable: ofrecer maquinaria industrial que no solo cumpla, sino que supere las expectativas, respaldada siempre por honestidad, precisión y un servicio excepcional.
              </p>
            </div>
            <div className="relative">
              <img 
                src="/images/nosotros-team.jpg" 
                alt="Historia MAESI"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-blue-600 to-blue-800 p-8 rounded-xl shadow-xl text-white max-w-xs">
                <Factory className="w-12 h-12 mb-3" />
                <p className="font-semibold text-lg">
                  Especialistas en diseño y fabricación de maquinaria industrial personalizada
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Evolución Timeline */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestra <span className="text-blue-600">Evolución</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un recorrido de crecimiento, innovación y compromiso constante
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {evolucion.map((etapa, index) => (
              <div key={index} className="relative h-full">
                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center mb-6 text-white font-bold text-xl">
                    {index + 1}
                  </div>
                  <div className="text-blue-600 font-bold text-lg mb-2">{etapa.period}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{etapa.title}</h3>
                  <p className="text-gray-600 leading-relaxed flex-grow">{etapa.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Misión y Visión */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Misión */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-12 rounded-2xl shadow-xl text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <Target className="w-16 h-16 mb-6" />
                <h3 className="text-3xl font-bold mb-4">Nuestra Misión</h3>
                <p className="text-lg text-blue-100 leading-relaxed">
                  {mision}
                </p>
              </div>
            </div>

            {/* Visión */}
            <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-12 rounded-2xl shadow-xl text-white relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400 opacity-5 rounded-full -ml-32 -mb-32"></div>
              <div className="relative z-10">
                <Eye className="w-16 h-16 mb-6" />
                <h3 className="text-3xl font-bold mb-4">Nuestra Visión</h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {vision}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Valores que nos <span className="text-blue-600">Definen</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Principios fundamentales que guían cada decisión y acción en MAESI
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {valores.map((valor) => {
              const IconComponent = valor.icon === 'Shield' ? Shield : valor.icon === 'Award' ? Award : Target;
              return (
                <div 
                  key={valor.id}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-blue-600"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center mb-6 transform rotate-3 hover:rotate-0 transition-transform">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{valor.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{valor.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">MAESI en Números</h2>
            <p className="text-xl text-blue-100">
              Resultados que respaldan nuestra trayectoria
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center p-6 bg-white/10 rounded-xl backdrop-blur-sm">
                <div className="text-5xl md:text-6xl font-bold mb-2">
                  {stat.number}
                </div>
                <div className="text-blue-100 font-medium text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciadores */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Lo Que Nos Hace <span className="text-blue-600">Únicos</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ventajas competitivas que nos distinguen en el mercado
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4 p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-100 hover:border-blue-600 transition-all duration-300">
              <CheckCircle2 className="w-12 h-12 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Personalización Total</h3>
                <p className="text-gray-600 leading-relaxed">
                  Cada proyecto es único. Diseñamos y fabricamos maquinaria adaptada específicamente a las necesidades de tu proceso productivo.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-100 hover:border-blue-600 transition-all duration-300">
              <Users className="w-12 h-12 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Atención Cercana</h3>
                <p className="text-gray-600 leading-relaxed">
                  Relación directa con ingenieros especializados. Acompañamiento en cada etapa del proyecto, desde el diseño hasta el soporte continuo.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-100 hover:border-blue-600 transition-all duration-300">
              <Award className="w-12 h-12 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Calidad-Precio Superior</h3>
                <p className="text-gray-600 leading-relaxed">
                  Mejor relación calidad-precio del mercado. Productos de primera calidad sin comprometer tu presupuesto.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-100 hover:border-blue-600 transition-all duration-300">
              <Lightbulb className="w-12 h-12 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Innovación Continua</h3>
                <p className="text-gray-600 leading-relaxed">
                  Inversión constante en tecnología y capacitación. Soluciones que incorporan las últimas tendencias industriales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
